import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

async function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  if (auth.currentUser) return auth.currentUser;
  return new Promise((resolve) => {
    let resolved = false;
    let unsubscribe: (() => void) | null = null;
    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) unsubscribe();
      resolve(null);
    }, timeoutMs);
    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      if (unsubscribe) unsubscribe();
      resolve(user);
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export interface FormulaVolatility {
  formulaId: number;
  formulaCode: string;
  formulaName: string;
  sigma: number | null;
  mu: number | null;
  dataPoints: number | null;
  updatedAt: string | null;
  priceCount: number;
  dateFrom: string | null;
  dateTo: string | null;
  calcDurationMs: number | null;
  calcEnabled: boolean;
}

export interface CalculateAllResult {
  success: boolean;
  duration: string;
  summary: {
    total: number;
    calculated: number;
    skipped: number;
  };
  formulas: FormulaVolatility[];
}

export async function getVolatilities(): Promise<FormulaVolatility[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v2/risk/volatility`, { headers });
  if (!res.ok) throw new Error(`Failed to fetch volatilities: ${res.statusText}`);
  const data = await res.json();
  return data.formulas;
}

export async function calculateAllVolatilities(): Promise<CalculateAllResult> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v2/risk/calculate-volatility`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) throw new Error(`Failed to calculate volatilities: ${res.statusText}`);
  return res.json();
}

export async function calculateFormulaVolatility(formulaId: number): Promise<FormulaVolatility> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v2/risk/calculate-volatility/${formulaId}`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) throw new Error(`Failed to calculate volatility: ${res.statusText}`);
  const data = await res.json();
  return data.formula;
}

export async function toggleFormulaCalc(formulaId: number, enabled: boolean): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v2/risk/volatility/${formulaId}/calc`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) throw new Error(`Failed to toggle calc: ${res.statusText}`);
}
